import React, { useEffect, useState } from "react"
import PubSub from "pubsub-js"
import { Bars3Icon, MagnifyingGlassIcon, ChatBubbleOvalLeftIcon, LifebuoyIcon, BuildingStorefrontIcon } from "@heroicons/react/24/outline"
import { usePageContext } from "vike-react/usePageContext"
import Logo from "../Logo"
import SearchBox from "./SearchBox"
import LanguageSwitcher from "./LanguageSwitcher"
import ProfileButton from "./ProfileButton"
import CartButton from "./CartButton"
import MobileMenu from "./MobileMenu"
import Button from "aldoo-ra/Button"
import Value from "aldoo-ra/Value"
import CategoriesMenu from "./CategoriesMenu"
import useT from "../Storefront/hooks/useT"
import useClientAuth from "../Storefront/hooks/useClientAuth"
import { stripLang } from "../Storefront/utils/contentRoutes"

/**
 * aldoo.com top bar: dark 60px bar (logo, search, language, sign-in /
 * profile, cart) plus a white 48px secondary bar with the "Categories" pill
 * on md+. Below lg the search collapses to an icon that opens a full-screen
 * overlay; below md a hamburger opens the browse drawer.
 */
function Navbar() {
  const t = useT()
  const pageContext = usePageContext()
  const currentPath = pageContext?.currentPath || pageContext?.urlPathname || "/"
  const [menuOpen, setMenuOpen] = useState(false)
  // the merchant portal's sections (Vendor/index.jsx): their badges mark the ☰
  const [portalMenu] = Value("vendor-portal-menu")
  const [searchOpen, setSearchOpen] = useState(false)
  const [unread, setUnread] = useState(0)
  const { auth } = useClientAuth()

  useEffect(() => {
    const token = PubSub.subscribe("unread-messages", (_, count) => setUnread(count || 0))
    return () => PubSub.unsubscribe(token)
  }, [])

  // "s" opens the search (old shortcut)
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "s" || e.ctrlKey || e.metaKey || e.altKey) return
      const el = document.activeElement
      if (el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable)) return
      e.preventDefault()
      setSearchOpen(true)
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [])

  useEffect(() => setSearchOpen(false), [pageContext.urlPathname])

  // the admin has its own header (a language prefix in the path too: /bg/admin)
  if (/^\/([a-z]{2}\/)?admin(\/|$)/.test(currentPath)) return null

  // the merchant portal is a workplace: no browsing pills, and no link to the
  // portal the merchant is already in
  const path = stripLang(currentPath)
  const inPortal = path === "/vendor" || path.startsWith("/vendor/")
  // a section of the portal waits for something (open orders, a missing detail): a red dot on the ☰
  const menuAlert = inPortal && !!portalMenu?.items?.some((item) => item.badge)
  // the portal's front page for a guest (MerchantLanding): no Support on top (the page has its own, at the
  // bottom), on phones Sign in in its corner; signed in, Support on top on phones only (the chat bubble is there on desktop)
  const portalGuest = inPortal && auth === null

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-[400] lg:h-[60px] bg-dark text-text_inv px-0 sm:px-4 lg:px-[60px]">
        <div className="mx-auto flex h-[60px] w-full max-w-w1_xl flex-row items-center gap-1 pr-2 sm:pr-0">
          {/* Mobile: hamburger + small logo */}
          <button 
            type="button" 
            onClick={() => setMenuOpen(true)} 
            aria-label={t`Menu`} 
            className="relative flex lg:hidden items-center justify-center w-12 h-12 text-text_inv">
            <Bars3Icon className="w-8 h-8" />
            {menuAlert && <span className="absolute top-2 right-1.5 w-3 h-3 rounded-full bg-error ring-2 ring-dark" data-testid="menu-alert-dot" />}
          </button>
          <Logo responsive />

          {/* Desktop: search always in the bar from lg up */}
          <div className="hidden lg:flex flex-1 items-center pl-5 pr-6">
            <SearchBox />
          </div>
          <div className="flex-1 lg:hidden" />

          {/* Right side */}
          {!inPortal && (
            <button
              type="button"
              onClick={() => PubSub.publish("show-chat")}
              aria-label={t`Support`}
              className="relative flex lg:hidden items-center justify-center w-11 h-11 text-text_inv"
            >
              <ChatBubbleOvalLeftIcon className="w-7 h-7" />
              {unread > 0 && (
                <span className="absolute top-0 right-0 min-w-[18px] h-[18px] rounded-full bg-error px-1 text-xs font-bold leading-[18px] text-center">{unread}</span>
              )}
            </button>
          )}
          {/* room before the cart: the search (flex-1) gives it up */}
          <div className="hidden lg:flex items-center gap-2 lg:mr-4">
            <LanguageSwitcher />
            <ProfileButton />
          </div>
          {/* the merchant portal buys nothing: Support (the chat, as the icon opens it) in the cart's place */}
          {portalGuest && (
            <Button
              variant="primary_s"
              onClick={() => PubSub.publish("signin", { redirect: "/vendor" })}
              className="lg:hidden shrink-0"
              data-testid="navbar-portal-signin"
            >
              {t`Sign in`}
            </Button>
          )}
          {inPortal ? (
            <button
              type="button"
              onClick={() => PubSub.publish("show-chat")}
              aria-label={t`Support`}
              className={`${portalGuest ? "hidden" : "flex lg:hidden"} relative items-center justify-center shrink-0 h-9 gap-1.5 pl-3 pr-3 lg:h-10 lg:gap-2 lg:pl-4 lg:pr-4 rounded-full border border-white/60 hover:border-white hover:bg-white/10 text-white transition-colors`}
              data-testid="navbar-support"
            >
              <ChatBubbleOvalLeftIcon className="w-5 h-5 lg:w-6 lg:h-6" />
              <span className="text-[14px] lg:text-[15px] font-semibold">{t`Support`}</span>
              {unread > 0 && (
                <span className="absolute -top-2 -right-2.5 min-w-[20px] h-5 px-1 rounded-full bg-admin_error text-white text-xs font-bold leading-5 text-center">{unread}</span>
              )}
            </button>
          ) : (
            <CartButton />
          )}
        </div>

        {/* phones: a search bar under the top row, the way the big shops do
            it; tapping it opens the full-screen search */}
        {!inPortal && (
          <div className="lg:hidden px-2 sm:px-0 pb-2.5">
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              aria-label={t`Search`}
              data-testid="mobile-search-bar"
              className="flex items-center w-full h-[40px] rounded-full bg-white/15 pl-4 pr-3 text-white/70 text-[16px] text-left"
            >
              <MagnifyingGlassIcon className="w-6 h-6 mr-2 shrink-0 text-white" />
              <span className="truncate">{t`Search anything...`}</span>
            </button>
          </div>
        )}

        {searchOpen && <SearchBox overlay open onClose={() => setSearchOpen(false)} className="lg:hidden" />}
      </header>

      {/* Secondary bar: "Categories" pill (desktop, from 1080px) */}
      {!inPortal && <div data-fixed-bar className="fixed top-[60px] left-0 right-0 z-[399] hidden lg:flex h-[48px] items-center border-b border-bordered bg-paper px-[60px]">
        <div className="mx-auto flex w-full max-w-w1_xl items-center gap-2">
          {/* opens the categories menu on hover (desktop); a click is still the categories page */}
          <CategoriesMenu />
          <span className="flex-1" />
          {/* on the right, under the search: help, and selling on aldoo.com (the merchant portal) */}
          <Button 
            variant="none" 
            href="/help-center" 
            className="flex items-center gap-2 h-9 px-3 rounded-full text-sm font-semibold text-text no-underline hover:bg-neutral" 
            data-testid="navbar-help"
          >
            <LifebuoyIcon className="w-5 h-5" aria-hidden="true" />
            {t`Help Center`}
          </Button>
          <Button 
            variant="none" 
            href="/vendor?choose=1" 
            className="flex items-center gap-2 h-9 px-4 rounded-full border-2 border-primary text-sm font-semibold text-primary no-underline hover:bg-primary hover:text-white transition-colors" 
            data-testid="navbar-sell"
          >
            {t`Sell in Aldoo`}
            <BuildingStorefrontIcon className="w-5 h-5" aria-hidden="true" />
          </Button>
        </div>
      </div>}

      {/* Keeps page content below the fixed bars */}
      <div className={inPortal ? "h-[60px]" : "h-[110px] lg:h-[108px]"} aria-hidden="true" />

      <MobileMenu open={menuOpen} onClose={() => setMenuOpen(false)} />
    </>
  )
}

// AldooComponent definition for auto-discovery
export const AldooComponent = {
  id: "Navbar",
  component: Navbar,
  description: "aldoo.com top bar: logo, search with suggestions, language switcher, sign-in/profile, cart. Auto-hides on admin routes.",
  input: {},
}

export default Navbar
